import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PauseCircle, PlayCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

export default function BulkPauseEditor({ services, onUpdated }) {
  const [selected, setSelected] = useState([]);
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const allSelected = services.length > 0 && selected.length === services.length;

  const apply = async (pauseDate) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/services/bulk-pause`, {
        service_ids: selected,
        unavailable_until: pauseDate || null,
      });
      onUpdated(r.data.services);
      toast.success(pauseDate
        ? `${r.data.updated_count} service(s) en pause jusqu'au ${fmt(pauseDate)}`
        : `${r.data.updated_count} service(s) réactivé(s)`);
      setSelected([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bulk-pause-editor" className="bg-amber-500/[0.06] border border-amber-500/25 rounded-md p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-amber-200 text-sm font-semibold flex items-center gap-2">
          <PauseCircle size={15} /> Mode vacances — pause groupée
        </p>
        <button
          type="button"
          onClick={() => setSelected(allSelected ? [] : services.map((s) => s.id))}
          data-testid="bulk-pause-toggle-all"
          className="text-white/60 hover:text-white text-xs underline"
        >
          {allSelected ? "Tout décocher" : "Tout cocher"}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-1.5">
        {services.map((s) => (
          <label
            key={s.id}
            data-testid={`bulk-pause-item-${s.id}`}
            className="flex items-center gap-2 bg-[#121212] border border-white/[0.06] rounded px-2.5 py-2 cursor-pointer hover:border-white/15 transition-colors"
          >
            <input
              type="checkbox"
              checked={selected.includes(s.id)}
              onChange={() => toggle(s.id)}
              data-testid={`bulk-pause-checkbox-${s.id}`}
              className="accent-[#FF3B30]"
            />
            <span className="text-white text-xs flex-1 truncate">{s.name}</span>
            {s.unavailable_until && (
              <span className="text-[10px] text-amber-300 bg-amber-500/15 px-1.5 py-0.5 rounded shrink-0">
                Reprise {fmt(s.unavailable_until)}
              </span>
            )}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="bulk-pause-date"
          className="bg-[#121212] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
          style={{ minHeight: 40 }}
        />
        <button
          type="button"
          onClick={() => apply(date)}
          disabled={busy || !date || selected.length === 0}
          data-testid="bulk-pause-apply-btn"
          className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 disabled:bg-white/10 disabled:text-white/40 text-black text-xs font-bold uppercase tracking-wider px-3 py-2.5 rounded transition-colors"
        >
          <PauseCircle size={13} /> Mettre en pause ({selected.length})
        </button>
        <button
          type="button"
          onClick={() => apply(null)}
          disabled={busy || selected.length === 0}
          data-testid="bulk-pause-resume-btn"
          className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.12] disabled:opacity-40 text-white text-xs font-bold uppercase tracking-wider px-3 py-2.5 rounded transition-colors"
        >
          <PlayCircle size={13} /> Réactiver la sélection
        </button>
      </div>
    </div>
  );
}
